import React, { useState, useEffect } from 'react'
import { router, usePage } from '@inertiajs/react'
import { DragDropContext, Droppable, Draggable, type DropResult } from '@hello-pangea/dnd'
import Layout from '../../components/Layout'
import ActionItemRow from '../../components/action_items/ActionItemRow'
import { ActionItemTableHeader } from '../../components/action_items/ActionItemTable'
import ActionItemFilters from '../../components/action_items/ActionItemFilters'
import ActionItemModal from '../../components/action_items/ActionItemModal'
import InlineAddRow from '../../components/action_items/InlineAddRow'
import type { ActionItem, ActionItemFilters as Filters, ActionItemCounts, User, SharedProps } from '../../types'
import type { ActionItemSourceOptions } from '../../constants/actionItemSources'

interface Props {
  team: { id: number; name: string; description: string | null; member_count: number }
  action_items: ActionItem[]
  team_members: User[]
  filters: Filters
  counts: ActionItemCounts
  can_create: boolean
  source_options: ActionItemSourceOptions
  highlight_id?: number | null
  active_tab: string
}

export default function ActionItems({ team, action_items, team_members, filters, counts, can_create, source_options, highlight_id, active_tab }: Props) {
  const { current_user } = usePage().props as unknown as SharedProps
  const [modalOpen, setModalOpen] = useState(false)
  const [editingItem, setEditingItem] = useState<ActionItem | null>(null)
  const [highlightedId, setHighlightedId] = useState<number | null>(null)

  // Deep-link from the Guide dashboard roll-up: scroll the targeted action item
  // into view and flash a highlight so it's easy to spot among many rows.
  useEffect(() => {
    if (!highlight_id) return
    setHighlightedId(highlight_id)
    const el = document.getElementById(`action-item-${highlight_id}`)
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' })
    const timer = setTimeout(() => setHighlightedId(null), 3000)
    return () => clearTimeout(timer)
  }, [highlight_id])

  // editingItem is a snapshot captured when the modal opened. After an Inertia
  // reload (e.g. a checklist edit), the fresh server data lives in action_items,
  // so re-resolve the item by id to keep the open modal in sync.
  const liveEditingItem = editingItem
    ? action_items.find(i => i.id === editingItem.id) ?? editingItem
    : null

  function handleEdit(item: ActionItem) {
    setEditingItem(item)
    setModalOpen(true)
  }

  function handleCloseModal() {
    setModalOpen(false)
    setEditingItem(null)
  }

  // Task #478: drag-and-drop is only live while Manual sort is selected;
  // every other sort renders a read-only ordering.
  const manualSort = filters.sort === 'manual'
  const [manualItems, setManualItems] = useState<ActionItem[]>(action_items)

  useEffect(() => {
    setManualItems(action_items)
  }, [action_items])

  const displayItems = manualSort ? manualItems : action_items

  function handleDragEnd(result: DropResult) {
    if (!result.destination || result.destination.index === result.source.index) return
    const previous = manualItems
    const next = Array.from(manualItems)
    const [moved] = next.splice(result.source.index, 1)
    next.splice(result.destination.index, 0, moved)
    setManualItems(next)
    router.put(`/teams/${team.id}/action_items/reorder`, {
      action_item_ids: next.map(i => i.id),
      ...filters,
    }, {
      preserveScroll: true,
      preserveState: true,
      onError: () => setManualItems(previous),
    })
  }

  return (
    <Layout team={team} activeTab={active_tab} actionItemCount={counts.open}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-semibold" style={{ color: '#1B1B1B' }}>Action Items</h1>
        {can_create && (
          <button
            onClick={() => { setEditingItem(null); setModalOpen(true) }}
            className="flex items-center gap-2 px-5 py-2.5 text-base font-medium text-white rounded-lg transition-colors"
            style={{ backgroundColor: '#ED1C24' }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            New Action Item
          </button>
        )}
      </div>

      <ActionItemFilters
        teamId={team.id}
        filters={filters}
        counts={counts}
        teamMembers={team_members}
      />

      <div className="mt-4 bg-white rounded-lg border border-gray-200 overflow-x-auto">
        {displayItems.length > 0 && <ActionItemTableHeader />}

        {displayItems.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="mx-auto mb-3" width="56" height="56" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <line x1="9" y1="9" x2="15" y2="15" />
              <line x1="15" y1="9" x2="9" y2="15" />
            </svg>
            <p className="text-text-secondary text-lg">No action items found.</p>
            <p className="text-text-muted text-base mt-1">
              {(filters.status || filters.search || filters.owner_id) ? 'Try adjusting your filters.' : 'Create one to get started.'}
            </p>
          </div>
        ) : manualSort ? (
          <DragDropContext onDragEnd={handleDragEnd}>
            <Droppable droppableId="team-action-items" direction="vertical">
              {(dropProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {displayItems.map((item, index) => (
                    <Draggable key={item.id} draggableId={String(item.id)} index={index}>
                      {(dragProvided, snapshot) => (
                        <div
                          ref={dragProvided.innerRef}
                          {...dragProvided.draggableProps}
                          {...dragProvided.dragHandleProps}
                          className={snapshot.isDragging ? 'shadow-lg bg-white' : ''}
                        >
                          <ActionItemRow
                            item={item}
                            teamId={team.id}
                            teamMembers={team_members}
                            onEdit={handleEdit}
                            highlighted={item.id === highlightedId}
                          />
                        </div>
                      )}
                    </Draggable>
                  ))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        ) : (
          displayItems.map(item => (
            <ActionItemRow
              key={item.id}
              item={item}
              teamId={team.id}
              teamMembers={team_members}
              onEdit={handleEdit}
              highlighted={item.id === highlightedId}
            />
          ))
        )}

        {can_create && (
          <InlineAddRow
            teamId={team.id}
            teamMembers={team_members}
            currentUserId={current_user.id}
          />
        )}
      </div>

      <ActionItemModal
        isOpen={modalOpen}
        onClose={handleCloseModal}
        teamId={team.id}
        teamMembers={team_members}
        currentUserId={current_user.id}
        existingItem={liveEditingItem}
        sourceOptions={source_options}
      />
    </Layout>
  )
}
